import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { FiLock } from 'react-icons/fi';

import { Formik } from 'formik';
import helpers from 'helpers';
import * as Yup from 'yup';

import { useReduxDispatch } from 'hooks/useReduxDispatch';
import { useReduxSelector } from 'hooks/useReduxSelector';
import useToast from 'hooks/useToast';

import ComponentButtonBase from 'components/button/Base';
import ComponentInputPassword from 'components/input/Password';
import ComponentModalBase, {
  IComponentModalBaseRefProps,
} from 'components/modal/Base';
import { Content, Title } from 'components/modal/styles';

import { userActions } from 'store/slices/user';
import userSelectors from 'store/slices/user/selectors';

import colors from 'styles/colors';
import Forms from 'styles/forms';

import { Actions, Bold, Message } from './styles';

export interface IUserChangePasswordData {
  id: number;
  name: string;
}

export interface IUserChangePasswordRefProps {
  close(): void;
  open(data: IUserChangePasswordData): void;
}

interface IUserChangePasswordFormData {
  confirmNewPassword: string;
  newPassword: string;
}

const initialValues: IUserChangePasswordFormData = {
  newPassword: '',
  confirmNewPassword: '',
};

const userChangePasswordValidateSchema = Yup.object().shape({
  newPassword: Yup.string().trim().required('Informe a nova senha'),
  confirmNewPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref('newPassword'), null], 'Senhas são diferentes')
    .required('Informe a confirmação da nova senha'),
});

const UserChangePassword: React.ForwardRefRenderFunction<
  IUserChangePasswordRefProps
> = (_, ref) => {
  const toast = useToast();
  const reduxDispatch = useReduxDispatch();

  const isLoading = useReduxSelector(userSelectors.changePasswordIsLoading);

  const [hideModal, setHideModal] = useState<boolean>(false);
  const [item, setItem] = useState<IUserChangePasswordData>(
    {} as IUserChangePasswordData,
  );

  const componentModalBaseRef = useRef<IComponentModalBaseRefProps>(null);
  const openModal = useCallback((data: IUserChangePasswordData) => {
    setItem(data);
    setHideModal(false);
    componentModalBaseRef.current?.open();
  }, []);

  const closeModal = useCallback(() => {
    setHideModal(true);
    componentModalBaseRef.current?.close();
  }, []);

  const handleChangePassword = useCallback(
    (data: IUserChangePasswordFormData) => {
      reduxDispatch(
        userActions.changePasswordRequest({
          data: {
            id: item.id,
            password: data.newPassword,
          },
          functions: {
            error: (err: any) => {
              helpers.errorHandling(err);
            },
            success: (message: string) => {
              toast.show({
                type: 'success',
                title: message,
              });
            },
            closeForm: () => {
              closeModal();
            },
          },
        }),
      );
    },
    [closeModal, item.id, reduxDispatch, toast],
  );

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  return (
    <ComponentModalBase ref={componentModalBaseRef}>
      <Content className={hideModal ? 'hideModal' : ''}>
        <Title>Alterar senha</Title>

        <Message>
          Preencha os campos abaixo para alterar a senha do usuário
          <Bold> {item.name}</Bold>
        </Message>

        <Formik
          initialValues={initialValues}
          onSubmit={handleChangePassword}
          validateOnChange={false}
          validationSchema={userChangePasswordValidateSchema}
        >
          {({ errors, handleChange, values }) => (
            <Forms.Content>
              <Forms.Grid>
                <ComponentInputPassword
                  errorMessage={errors.newPassword}
                  hasError={!!errors.newPassword}
                  icon={FiLock}
                  onChange={handleChange('newPassword')}
                  placeholder="Nova senha"
                  value={values.newPassword}
                />

                <ComponentInputPassword
                  errorMessage={errors.confirmNewPassword}
                  hasError={!!errors.confirmNewPassword}
                  icon={FiLock}
                  onChange={handleChange('confirmNewPassword')}
                  placeholder="Confirmar nova senha"
                  value={values.confirmNewPassword}
                />
              </Forms.Grid>
              <Actions>
                <ComponentButtonBase
                  backgroundColor={colors.red500}
                  disabled={isLoading}
                  onClick={closeModal}
                  type="button"
                >
                  Cancelar
                </ComponentButtonBase>
                <ComponentButtonBase
                  backgroundColor={colors.orange700}
                  disabled={isLoading}
                  isLoading={isLoading}
                  type="submit"
                >
                  Confirmar
                </ComponentButtonBase>
              </Actions>
            </Forms.Content>
          )}
        </Formik>
      </Content>
    </ComponentModalBase>
  );
};

export default forwardRef(UserChangePassword);
